"use client";
import React, { useContext, useEffect, useMemo, useState } from "react";
import Script from "next/script";

import useActionEvents from "@stadium/services/analytics/hooks/use-action-events";
import useUserIdentification from "@stadium/services/analytics/hooks/use-user-identification";

import { GetDeviceType } from "@stadium/helpers/device";
import { getPersonas } from "@stadium/services/june-analytics/helpers";
import { getIsUserBlacklisted } from "@stadium/services/analytics/helpers/blacklisted-users";

import { juneSoScriptSrc } from "@stadium/services/june-analytics/constants";
import { JUNE_SO } from "@stadium/services/analytics/constants/services";

const defaultObj = {};

export const JuneSoContext = React.createContext(defaultObj);

export const useJuneSoContext = () => useContext(JuneSoContext);

const defaultCurrentUserData = {
  email: "",
  firstName: "",
  lastName: "",
  spreeId: "",
  company: defaultObj,
  profile: defaultObj,
};

export const JuneSoProvider = ({ children, apiKey }) => {
  const [isJuneSoScriptLoaded, setIsJuneSoScriptLoaded] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(defaultCurrentUserData);
  const [isUserDataLoaded, setIsUserDataLoaded] = useState(false);
  const [pageName, setPageName] = useState("");

  const services = [JUNE_SO];
  const { identifyUser, groupUser } = useUserIdentification(services);
  const { trackEvent } = useActionEvents(services);

  useEffect(() => {
    if (isJuneSoScriptLoaded && isUserDataLoaded) {
      const { email, firstName, lastName, spreeId: userId, company, profile } = currentUserData;
      const personas = getPersonas(profile);
      identifyUser({
        userId,
        userProperties: { email, name: `${firstName} ${lastName}`, personas },
        additionalProps: { companyName: company?.name },
      });

      trackEvent({
        eventName: "logged_in",
        eventProperties: {
          email,
          name: `${firstName} ${lastName}`,
          device_type: GetDeviceType(),
          referrer: document?.referrer,
        },
        context: { groupId: company?.id },
        additionalProps: { companyName: company?.name },
      });
      groupUser({ companyId: company?.id, companyProperties: { name: company?.name } });
    }
  }, [isJuneSoScriptLoaded, isUserDataLoaded]);

  useEffect(() => {
    if (!window?.analytics) {
      window.analytics = {};
      window.analytics._writeKey = apiKey;
    }
  }, []);

  const onLoad = () => {
    setIsJuneSoScriptLoaded(true);
  };

  const value = useMemo(
    () => ({
      setCurrentUserData,
      setIsUserDataLoaded,
      isUserDataLoaded,
      isJuneSoScriptLoaded,
      isJuneSoSetupCompleted: isJuneSoScriptLoaded && isUserDataLoaded,
      setPageName,
      pageName,
      currentUserData,
      isLoggedInUserBlackListed: getIsUserBlacklisted({
        companyName: currentUserData?.company?.name,
      }),
    }),
    [isUserDataLoaded, isJuneSoScriptLoaded, pageName, currentUserData],
  );

  return (
    <>
      <Script
        type="application/javascript"
        onLoad={onLoad}
        src={juneSoScriptSrc}
        strategy="lazyOnload"
      />
      <JuneSoContext.Provider value={value}>{children}</JuneSoContext.Provider>
    </>
  );
};
